import React from 'react';
import { DiagnosticOutDTO, DiagnosticSeverityEnum } from '@api/ringil-api.v2';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { diagnosticHint, diagnosticMessage } from '../i18nKeys';

interface ComponentProps {
  diagnostics: DiagnosticOutDTO[];
}

/**
 * Errors and warnings from validate / format / dry-run.
 *
 * The message says what is wrong and the hint says what to do instead — separate on purpose, so
 * both are rendered rather than concatenated. Errors block saving; warnings do not.
 */
const DiagnosticsPanel: React.FC<ComponentProps> = ({ diagnostics }) => {
  const { tr } = useTranslationLgs();

  if (diagnostics.length === 0) {
    return <div className="rounded-md border border-solid border-base-tertiary p-3 text-sm text-content-secondary">{tr('AiDispatcher.noDiagnostics', 'No problems found.')}</div>;
  }

  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {diagnostics.map((diagnostic, index) => {
        const isError = diagnostic.diagnosticSeverity === DiagnosticSeverityEnum.ERROR;
        const hint = diagnosticHint(tr, diagnostic);
        return (
          <li key={index} className={`rounded-md border border-solid p-3 text-sm ${isError ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className={`text-xs font-medium uppercase ${isError ? 'text-red-700' : 'text-amber-700'}`}>
                {isError ? tr('AiDispatcher.error', 'Error') : tr('AiDispatcher.warning', 'Warning')}
              </span>
              {/* line 0 means the diagnostic came from a builder edit and has nowhere to point. */}
              {diagnostic.line > 0 && (
                <span className="font-mono text-xs text-content-tertiary">
                  {tr('AiDispatcher.lineColumn', 'line {{line}}, column {{column}}', { line: diagnostic.line, column: diagnostic.column })}
                </span>
              )}
            </div>
            <div className="mt-1">{diagnosticMessage(tr, diagnostic)}</div>
            {hint && <div className="mt-1 text-content-secondary">{hint}</div>}
          </li>
        );
      })}
    </ul>
  );
};

export default DiagnosticsPanel;
